class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._onViewportResize = this._syncViewportHeight.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  _syncViewportHeight() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }

  _bindViewportHeightListeners() {
    this._syncViewportHeight();
    window.addEventListener('resize', this._onViewportResize);
    window.visualViewport?.addEventListener('resize', this._onViewportResize);
  }

  _unbindViewportHeightListeners() {
    window.removeEventListener('resize', this._onViewportResize);
    window.visualViewport?.removeEventListener('resize', this._onViewportResize);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.dataset.activeTab = '#cart-products';
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.dataset.activeTab = '#cart-products';
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    document.querySelector('sticky-header')?.onHeaderOverlayOpening?.();
    this._bindViewportHeightListeners();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const presetTab = this.dataset.activeTab || '#cart-products';
    if (typeof window.activateCartDrawerTab === 'function') {
      window.activateCartDrawerTab(presetTab, {
        renderWishlist: presetTab === '#cart-wishlist',
        immediate: presetTab === '#cart-wishlist',
      });
    }

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    });

    this.addEventListener(
      'transitionend',
      () => {
        const wishlistTab = this.querySelector('#cart-wishlist');
        const isWishlistActive = wishlistTab && !wishlistTab.classList.contains('hidden');
        let containerToTrapFocusOn = document.getElementById('CartDrawer');

        if (isWishlistActive) {
          containerToTrapFocusOn = wishlistTab;
        } else if (this.classList.contains('is-empty')) {
          containerToTrapFocusOn =
            this.querySelector('.cart-drawer__empty-state') ||
            this.querySelector('#cart-products') ||
            this.querySelector('.drawer__inner');
        }

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (this.activeElement?.closest?.('custom-quick-add-overlay, cart-size-selector')) {
      this.activeElement = null;
    }
    if (typeof window.hideAllOpenQuickAddOverlays === 'function') {
      window.hideAllOpenQuickAddOverlays();
    }
    if (typeof window.closeCartDrawerSizeSelectors === 'function') {
      window.closeCartDrawerSizeSelectors(this);
    }
    this._unbindViewportHeightListeners();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.dispatchEvent(new CustomEvent('cart-drawer:close'));
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      document.dispatchEvent(new CustomEvent('cart-drawer:updated'));
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: 'cart-drawer-items',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__footer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function setupCartDrawerTabs() {
  window.cartDrawerTabClickHandler = tabClickHandler;

  const cartDrawerRoot = document.querySelector('cart-drawer');
  if (cartDrawerRoot && cartDrawerRoot.dataset.tabsDelegated !== 'true') {
    cartDrawerRoot.addEventListener('click', function (event) {
      const heading = event.target.closest('.js-drawer-heading');
      if (!heading || !cartDrawerRoot.contains(heading)) {
        return;
      }
      tabClickHandler.call(heading, event);
    });
    cartDrawerRoot.dataset.tabsDelegated = 'true';
  }

  let cartUpdateDebounceTimer = null;

  function handleCartStateUpdate(immediate) {
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && cartDrawer.dataset.pendingWishlistSwitch === 'true') {
      if (cartUpdateDebounceTimer) {
        clearTimeout(cartUpdateDebounceTimer);
        cartUpdateDebounceTimer = null;
      }
      return;
    }

    if (immediate) {
      if (cartUpdateDebounceTimer) {
        clearTimeout(cartUpdateDebounceTimer);
        cartUpdateDebounceTimer = null;
      }
      initTabs();
      if (typeof window.initCartDrawerWishlistButtons === 'function') {
        window.initCartDrawerWishlistButtons();
      }
      if (typeof window.initCartDrawerSizeSelectors === 'function') {
        window.initCartDrawerSizeSelectors(document.querySelector('cart-drawer'));
      }
      return;
    }

    if (cartUpdateDebounceTimer) {
      clearTimeout(cartUpdateDebounceTimer);
    }
    cartUpdateDebounceTimer = setTimeout(function () {
      cartUpdateDebounceTimer = null;
      initTabs();
      if (typeof window.initCartDrawerWishlistButtons === 'function') {
        window.initCartDrawerWishlistButtons();
      }
      if (typeof window.initCartDrawerSizeSelectors === 'function') {
        window.initCartDrawerSizeSelectors(document.querySelector('cart-drawer'));
      }
    }, 60);
  }

  initTabs();

  document.addEventListener('cart-drawer:open', function () {
    initTabs();

    const cartDrawer = document.querySelector('cart-drawer');
    if (
      cartDrawer &&
      cartDrawer.dataset.activeTab === '#cart-wishlist' &&
      typeof window.swymRenderWishlist === 'function' &&
      window._swat
    ) {
      window.swymRenderWishlist(window._swat);
    } else {
      if (typeof window.initCartDrawerWishlistButtons === 'function') {
        window.initCartDrawerWishlistButtons();
      }
    }
    if (typeof window.initCartDrawerSizeSelectors === 'function') {
      window.initCartDrawerSizeSelectors(document.querySelector('cart-drawer'));
    }
  });

  document.addEventListener('cart:refresh', function () {
    handleCartStateUpdate(false);
  });

  document.addEventListener('cart-drawer:updated', function () {
    handleCartStateUpdate(true);
  });

  if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (event) {
      handleCartStateUpdate(true);
    });
  }

  const originalFetch = window.fetch;
  window.fetch = function () {
    const fetchPromise = originalFetch.apply(this, arguments);
    const url = arguments[0];

    if (
      typeof url === 'string' &&
      (url.includes('/cart/add') ||
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear'))
    ) {
      fetchPromise.then(function () {
        handleCartStateUpdate(false);
      });
    }

    return fetchPromise;
  };

  const originalXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function () {
    const method = arguments[0];
    const url = arguments[1];

    if (method && url && typeof url === 'string') {
      const oldOnReadyStateChange = this.onreadystatechange;
      this.onreadystatechange = function () {
        if (this.readyState === 4) {
          if (
            url.includes('/cart/add') ||
            url.includes('/cart/change') ||
            url.includes('/cart/update') ||
            url.includes('/cart/clear')
          ) {
            handleCartStateUpdate(false);
          }
        }
        if (oldOnReadyStateChange) {
          oldOnReadyStateChange.apply(this, arguments);
        }
      };
    }

    return originalXHROpen.apply(this, arguments);
  };

  function activateTab(targetId, options) {
    if (!targetId) {
      return;
    }

    options = options || {};

    const cartDrawer = document.querySelector('cart-drawer');
    const tabPanels = document.querySelector('.cart-drawer__tab-panels');
    const currentActivePanel = document.querySelector('#cart-products:not(.hidden), #cart-wishlist:not(.hidden)');
    const isActuallyChanging = !currentActivePanel || ('#' + currentActivePanel.id !== targetId);

    // If tab is already active and we don't need to render wishlist or force, avoid redundant actions
    if (!isActuallyChanging && !options.renderWishlist && !options.force) {
      if (cartDrawer) {
        cartDrawer.dataset.activeTab = targetId;
      }
      return;
    }

    if (cartDrawer) {
      cartDrawer.dataset.activeTab = targetId;
    }

    const isTabChange = options.animate && cartDrawer && tabPanels && isActuallyChanging;

    if (isTabChange) {
      tabPanels.style.setProperty('--cart-drawer-tab-height', `${tabPanels.offsetHeight}px`);
      cartDrawer.classList.add('cart-drawer--tab-switching');
    }

    document.querySelectorAll('.js-drawer-heading').forEach(function (tab) {
      tab.classList.toggle('active', tab.getAttribute('data-drawer') === targetId);
    });

    document.querySelectorAll('#cart-products, #cart-wishlist').forEach(function (content) {
      content.classList.toggle('hidden', '#' + content.id !== targetId);
    });

    if (
      options.renderWishlist &&
      targetId === '#cart-wishlist' &&
      typeof window.swymRenderWishlist === 'function' &&
      window._swat
    ) {
      window.swymRenderWishlist(window._swat, { immediate: options.immediate });
    }

    if (targetId === '#cart-wishlist') {
      document.dispatchEvent(new CustomEvent('cart-drawer:wishlist-tab-active'));
    } else if (targetId === '#cart-products') {
      const cartItemsContainer = document.getElementById('CartDrawer-CartItems');
      cartItemsContainer?.classList.remove('cart__items--disabled');

      if (typeof window.initCartDrawerWishlistButtons === 'function') {
        window.initCartDrawerWishlistButtons();
      }

      if (cartDrawer && typeof trapFocus === 'function') {
        const cartPanel = document.getElementById('cart-products');
        const focusTarget =
          cartPanel?.querySelector('.cart-item__name') ||
          cartPanel?.querySelector('.cart-item') ||
          cartPanel;
        if (focusTarget) {
          trapFocus(cartPanel, focusTarget);
        }
      }
    }

    if (isTabChange) {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          cartDrawer.classList.remove('cart-drawer--tab-switching');
          tabPanels.style.removeProperty('--cart-drawer-tab-height');
        });
      });
    }
  }

  window.activateCartDrawerTab = activateTab;

  window.openCartDrawerWishlistTab = function (options) {
    options = options || {};
    const cartDrawer = document.querySelector('cart-drawer');

    if (!cartDrawer) {
      return;
    }

    cartDrawer.dataset.activeTab = '#cart-wishlist';

    if (!cartDrawer.classList.contains('active')) {
      const trigger = options.trigger instanceof HTMLElement ? options.trigger : null;
      cartDrawer.open(trigger);
      return;
    }

    activateTab('#cart-wishlist', {
      renderWishlist: true,
      animate: options.animate !== false,
      immediate: options.immediate,
    });
  };

  function initTabs() {
    const cartDrawer = document.querySelector('cart-drawer');
    const tabHeadings = document.querySelectorAll('.js-drawer-heading');

    if (!tabHeadings.length) {
      return;
    }

    if (cartDrawer && cartDrawer.dataset.pendingWishlistSwitch === 'true') {
      return;
    }

    const activeHeading = document.querySelector('.js-drawer-heading.active');
    const activeTargetId =
      (cartDrawer && cartDrawer.dataset.activeTab) ||
      (activeHeading && activeHeading.getAttribute('data-drawer')) ||
      tabHeadings[0].getAttribute('data-drawer');

    activateTab(activeTargetId, { animate: false });
  }

  window.syncCartDrawerTabs = initTabs;

  window.syncCartDrawerHeadingCount = function (itemCount) {
    const cartTitle = document.querySelector('cart-drawer .cart_title');
    if (!cartTitle || typeof itemCount !== 'number') {
      return;
    }
    cartTitle.textContent = cartTitle.textContent.replace(/\(\d+\)/, '(' + itemCount + ')');
  };

  function tabClickHandler(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const targetId = this.getAttribute('data-drawer');
    if (!targetId) {
      return false;
    }

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.dataset.activeTab = targetId;
      }

      activateTab(targetId, {
        renderWishlist: targetId === '#cart-wishlist',
        animate: true,
      });
    } catch (e) {
      // Silently handle errors
    }

    return false;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupCartDrawerTabs);
} else {
  setupCartDrawerTabs();
}
